import { useState } from "react";
import { useTranslation } from "react-i18next";
import { List, X } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import {
  ROOT,
  AUTHENTICATE,
  AUTHENTICATE_SIGNUP,
  ABOUT,
  CONTACT,
} from "@/lib/links";
import { Button } from "@/components/ui/button";
import AccessibilitySettingsPopup from "@/components/AccessibilitySettingsPopup";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useA11ySettings } from "@/lib/hooks/useA11ySettings";

import { Outlet } from "react-router-dom";

interface MarketingLayoutProps {
  children?: React.ReactNode;
}

export default function MarketingLayout({ children }: MarketingLayoutProps) {
  const { t } = useTranslation(["newLanding", "common", "auth"]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { darkMode } = useA11ySettings();
  return (
    <TooltipProvider>
      <main className="flex flex-col w-full min-h-screen bg-background max-h-dvh">
        <nav className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="relative grid items-center w-full grid-cols-3 px-4 py-3 mx-auto md:py-4 max-w-7xl">
            {/* Left (desktop links / mobile menu button) */}
            <div className="flex items-center justify-self-start">
              <button
                type="button"
                onClick={() => setMobileNavOpen((o) => !o)}
                className="inline-flex items-center justify-center w-10 h-10 border rounded-md md:hidden border-border hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                aria-label={
                  mobileNavOpen ? t("common:close") : t("common:menu")
                }
                aria-expanded={mobileNavOpen}
                aria-controls="mobile-nav-panel"
              >
                {mobileNavOpen ? <X size={22} /> : <List size={22} />}
              </button>
              <div className="items-center hidden space-x-6 md:flex">
                {/* <Link
                  to="/about"
                  className="text-sm font-medium transition-colors text-muted-foreground hover:text-foreground"
                >
                  {t("common:about")}
                </Link>
                <Link
                  to="/contact"
                  className="text-sm font-medium transition-colors text-muted-foreground hover:text-foreground"
                >
                  {t("common:contact")}
                </Link> */}
                {/* <Link
                  to="https://whiteboard.tchr.tools"
                  target="_blank"
                  className="text-sm font-medium transition-colors text-muted-foreground hover:text-foreground"
                >
                  {t("common:blog")}
                </Link> */}
              </div>
            </div>
            {/* Center logo */}
            <Link
              to={ROOT}
              className="relative flex items-center font-semibold justify-self-center"
            >
              <img
                src={
                  darkMode
                    ? "/assets/TT logo white.png"
                    : "/assets/TT logo black.png"
                }
                alt="Logo"
                className="w-auto h-8"
              />
              <span className="sr-only">{t("nav.title")}</span>
            </Link>
            {/* Right (desktop actions) */}
            <div className="items-center hidden space-x-4 md:flex justify-self-end">
              <AccessibilitySettingsPopup />
              <Button asChild variant="outline" size="sm">
                <Link to={AUTHENTICATE}>{t("common:login")}</Link>
              </Button>
              <Button asChild size="sm">
                <Link to={AUTHENTICATE_SIGNUP}>{t("common:signup")}</Link>
              </Button>
            </div>
            {/* Mobile slide-down panel */}
            <div
              id="mobile-nav-panel"
              className={`md:hidden col-span-3 absolute top-full left-0 right-0 origin-top border-b border-border bg-background shadow-sm transition-all duration-200 ${
                mobileNavOpen
                  ? "animate-in slide-in-from-top-2 opacity-100"
                  : "pointer-events-none opacity-0 -translate-y-2"
              }`}
            >
              <div className="flex flex-col gap-1 p-4">
                <Link
                  to={ABOUT}
                  onClick={() => setMobileNavOpen(false)}
                  className="px-3 py-2 text-sm font-medium rounded-md hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  {t("common:about")}
                </Link>
                <Link
                  to={CONTACT}
                  onClick={() => setMobileNavOpen(false)}
                  className="px-3 py-2 text-sm font-medium rounded-md hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  {t("common:contact")}
                </Link>
                <div className="h-px my-2 bg-border" />
                <Button asChild variant="outline" className="justify-start">
                  <Link
                    to={AUTHENTICATE}
                    onClick={() => setMobileNavOpen(false)}
                  >
                    {t("common:login")}
                  </Link>
                </Button>
                <Button asChild className="justify-start">
                  <Link
                    to={AUTHENTICATE_SIGNUP}
                    onClick={() => setMobileNavOpen(false)}
                  >
                    {t("common:signup")}
                  </Link>
                </Button>
                <div className="h-px my-2 bg-border" />
                <AccessibilitySettingsPopup />
              </div>
            </div>
          </div>
        </nav>
        {/* Page content */}
        {children ? children : <Outlet />}
      </main>
    </TooltipProvider>
  );
}
